import { usePatientCard } from '../../../hooks/usePatientCard';
import { ScansCard } from './cards/ScansCard';
import { TreatmentCard } from './cards/TreatmentCard';
import { StatusCard } from './cards/StatusCard';
import { DatesCard } from './cards/DatesCard';
import { MonitoringCard } from './cards/MonitoringCard';
import { DmEventsCard } from './cards/DmEventsCard';
import { MondayNotesCard } from './cards/MondayNotesCard';
import { PatientNotesCard } from './cards/PatientNotesCard';

const SKELETON_CARD_COUNT = 6;

interface OverviewTabProps {
    patientId: string;
    onViewNotes?: () => void;
}

function OverviewSkeleton() {
    return (
        <div className="flex-1 overflow-auto p-5">
            <div className="grid grid-cols-2 gap-4">
                {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
                    <div key={i} className="h-32 rounded-xl bg-gray-100 animate-pulse" />
                ))}
            </div>
        </div>
    );
}

function OverviewError({ onRetry }: { onRetry: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
            <div className="text-xs text-red-500">Failed to load patient data</div>
            <button
                onClick={onRetry}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
                Retry
            </button>
        </div>
    );
}

function OverviewTab({ patientId, onViewNotes }: OverviewTabProps) {
    const { data, isLoading, error, refetch } = usePatientCard(patientId);

    if (isLoading) return <OverviewSkeleton />;
    if (error || !data) return <OverviewError onRetry={refetch} />;

    const hasDmId = data.monitoring?.dmId != null;

    return (
        <div className="flex-1 overflow-auto p-5">
            <div className="grid grid-cols-2 gap-4">
                {hasDmId && <TreatmentCard patientId={patientId} monitoring={data.monitoring} />}
                <div className={hasDmId ? '' : 'col-span-2'}>
                    <StatusCard status={data.status} />
                </div>
                <div className="col-span-2">
                    <PatientNotesCard patientId={patientId} onViewAll={onViewNotes} />
                </div>
                <div className="col-span-2">
                    <ScansCard
                        patientId={patientId}
                        monitoring={data.monitoring}
                        dates={data.dates}
                    />
                </div>
                <DatesCard dates={data.dates} />
                <MonitoringCard monitoring={data.monitoring} dates={data.dates} />
                <DmEventsCard patientId={patientId} dmId={data.monitoring?.dmId} />
                <MondayNotesCard notes={data.notes} />
            </div>
        </div>
    );
}

export { OverviewTab };
